import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Container, Card, Button, Alert, Modal, Form, Spinner, Badge } from 'react-bootstrap';
import { FiPlus, FiMapPin, FiEye, FiHome, FiSlash, FiCheckCircle, FiEdit2, FiCheckSquare, FiFileText } from 'react-icons/fi';
import TableActionButton from '@core/components/TableActionButton';
import TableActions from '@core/components/TableActions';
import StatusPill from '@core/components/StatusPill';
import { useDistricts } from '@features/district/hooks/useDistricts';
import { useDistrictActions } from '@features/district/hooks/useDistrictActions';
import { createDistrict, updateDistrict } from '@features/district/api/districtApi';
import { DataTable, DataTableColumn } from '@core/components/DataTable';
import { District } from '@features/district/types/district';
import { useLocation, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useErrorModalStore } from '@infrastructure/api/store/errorModalStore';
import { ErrorResponse } from '@core/types/errorResponse';
import { SearchBar, FilterField } from '@core/components/SearchBar';
import { useDebounce } from '@core/hooks/useDebounce';
import { useSearchUrlSync } from '@core/hooks/useSearchUrlSync';
import PaginationBar from '@core/components/PaginationBar';
import TablePaginationFooter, { getPaginationRange } from '@core/components/TablePaginationFooter';
import { buildDefaultListParams } from '@core/utils/listQuery';
import {
  NAME_SORT_FILTER_FIELD,
  SORT_BY_DISTRICT_NAME,
  SORT_DIR_ASC,
  countContentFilters,
  filtersWithoutSort,
  normalizeSortDir,
  readSortDirFromSearchParams,
  withCatalogSort,
} from '@core/constants/listSort';
import StatusChangeConfirmModal, { StatusChangeKind } from '@features/admin/finance/components/StatusChangeConfirmModal';
import { usePermissions } from '@features/entity/hooks/usePermissions';
import { ACTION_CODES, BULK_TEMPLATE_ASSIGN_CODES } from '@features/entity/types/actionCodes';
import { showSuccess } from '@infrastructure/api/utils/toastNotifications';
import { useApiError } from '@infrastructure/api/hooks/useApiError';
import { useBulkSelection } from '@core/hooks/useBulkSelection';
import BulkAssignTemplatesModal from '@features/church/components/BulkAssignTemplatesModal';
import DistrictTemplatesModal from '@features/district/components/DistrictTemplatesModal';

const DISTRICT_ACTION_CODES = [
  ACTION_CODES.CREATE_DISTRICT,
  ACTION_CODES.ENABLE_DISTRICT,
  ACTION_CODES.DISABLE_DISTRICT,
  ACTION_CODES.UPDATE_DISTRICT,
  ACTION_CODES.SYNC_DISTRICT_REPORT_TEMPLATE,
  ACTION_CODES.BULK_ASSIGN_OVERRIDE_DISTRICT_REPORT_TEMPLATE,
  ...BULK_TEMPLATE_ASSIGN_CODES,
];

const DISTRICT_FILTER_FIELDS: FilterField[] = [
  NAME_SORT_FILTER_FIELD,
  { name: 'districtName', label: 'District Name', type: 'text', placeholder: 'Filter by district name', colWidth: 6 },
  { name: 'churchName',   label: 'Church Name',   type: 'text', placeholder: 'Filter by church name',   colWidth: 6 },
  { name: 'isActive',     label: 'Status',        type: 'select', options: [{ value: '1', label: 'Active' }, { value: '0', label: 'Inactive' }], colWidth: 6 },
];

type CreateDistrictFormErrors = Partial<Record<'name' | 'description', string[]>>;

const NationDistrictsPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { nationId } = useParams<{ nationId: string }>();
  const nationName = location.state?.nationName || 'Nation';

  const [urlParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(() => urlParams.get('q') ?? '');
  const debouncedQuery = useDebounce(searchQuery, 300);
  const [activeFilters, setActiveFilters] = useState<Record<string, any>>(() => {
    const result: Record<string, any> = {};
    urlParams.forEach((v, k) => { if (k !== 'q' && k !== 'page') result[k] = v; });
    result.sortDir = readSortDirFromSearchParams(urlParams);
    return result;
  });
  const previousPathnameRef = useRef(location.pathname);

  useSearchUrlSync({ query: debouncedQuery, filters: activeFilters, page: 1 });

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [showViewModal, setShowViewModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [viewDistrict, setViewDistrict] = useState<District | null>(null);
  const [formData, setFormData] = useState<{ district_name: string; description: string }>({ district_name: '', description: '' });
  const [createData, setCreateData] = useState<{ name: string; description: string }>({ name: '', description: '' });
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [createFormErrors, setCreateFormErrors] = useState<CreateDistrictFormErrors>({});
  const [editFormErrors, setEditFormErrors] = useState<CreateDistrictFormErrors>({});

  const {
    districts,
    loading,
    error,
    updateParams,
    refetch,
  } = useDistricts(
    withCatalogSort(
      {
        ...buildDefaultListParams(
          {
            perPage: 100,
            ...(searchQuery ? { freetext: searchQuery } : {}),
            ...filtersWithoutSort(activeFilters),
          },
          ['freetext', 'districtName', 'churchName']
        ),
        sortDir: normalizeSortDir(activeFilters.sortDir),
      },
      SORT_BY_DISTRICT_NAME
    )
  );

  useEffect(() => {
    updateParams(
      withCatalogSort(
        {
          ...buildDefaultListParams(
            { freetext: debouncedQuery || undefined, ...filtersWithoutSort(activeFilters) },
            ['freetext', 'districtName', 'churchName']
          ),
          sortDir: normalizeSortDir(activeFilters.sortDir),
        },
        SORT_BY_DISTRICT_NAME
      )
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQuery, activeFilters]);

  useEffect(() => {
    if (previousPathnameRef.current === location.pathname) return;

    previousPathnameRef.current = location.pathname;
    setSearchQuery('');
    setActiveFilters({ sortDir: SORT_DIR_ASC });
    setCurrentPage(1);
    updateParams(
      withCatalogSort(
        {
          ...buildDefaultListParams({
            perPage: 100,
            page: 1,
            freetext: undefined,
            districtName: undefined,
            churchName: undefined,
          }, ['freetext', 'districtName', 'churchName']),
          sortDir: SORT_DIR_ASC,
        },
        SORT_BY_DISTRICT_NAME
      )
    );
  }, [location.pathname, updateParams]);

  // Scope the list to only this nation's districts (district/query returns all
  // visible districts; filter client-side so the count matches the dashboard).
  const numericNationId = Number(nationId) || 0;
  const scopedDistricts = numericNationId
    ? districts.filter((d) => Number(d.nation_id) === numericNationId)
    : districts;

  const totalPages = Math.max(1, Math.ceil(scopedDistricts.length / itemsPerPage));
  const paginatedDistricts = scopedDistricts.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  const { start, end } = getPaginationRange({
    page: currentPage,
    perPage: itemsPerPage,
    total: scopedDistricts.length,
    currentCount: paginatedDistricts.length,
  });

  const [statusConfirm, setStatusConfirm] = useState<{ district: District; kind: StatusChangeKind } | null>(null);
  const showError = useApiError();
  const { busy: statusBusy, doDistrictDisable, doDistrictEnable } = useDistrictActions();

  // ADMIN-only enable/disable gated on NATION-scoped action codes.
  const { hasPermission, hasAnyPermission } = usePermissions({
    scopeType: 'NATION',
    scopeId: numericNationId || undefined,
    actionCodes: DISTRICT_ACTION_CODES,
  });
  const canCreate = hasPermission(ACTION_CODES.CREATE_DISTRICT);
  const canDisable = hasPermission(ACTION_CODES.DISABLE_DISTRICT);
  const canEnable = hasPermission(ACTION_CODES.ENABLE_DISTRICT);
  const canEdit = hasPermission(ACTION_CODES.UPDATE_DISTRICT);
  const canManageTemplates = hasPermission(ACTION_CODES.SYNC_DISTRICT_REPORT_TEMPLATE);
  const canBulkAssign = hasAnyPermission(BULK_TEMPLATE_ASSIGN_CODES as unknown as string[]);

  const bulkSelection = useBulkSelection<District>();
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [selectedDistrictForTemplates, setSelectedDistrictForTemplates] = useState<District | null>(null);

  const handleOpenTemplates = (district: District) => {
    setSelectedDistrictForTemplates(district);
    setShowTemplateModal(true);
  };

  const selectedDistrictsForBulk = useMemo(() =>
    bulkSelection.getSelectedItems(scopedDistricts, (d) => d.district_id).map((d) => ({
      id: d.district_id,
      name: d.district_name,
    })),
    [bulkSelection, scopedDistricts]
  );

  const handleStatusConfirm = async () => {
    if (!statusConfirm || !numericNationId) return;
    const { district, kind } = statusConfirm;
    const response = kind === 'disable'
      ? await doDistrictDisable(numericNationId, district.district_id)
      : await doDistrictEnable(numericNationId, district.district_id);

    if (response.isSuccess()) {
      showSuccess(response.getData()?.message ?? `District ${kind}d successfully.`);
      setStatusConfirm(null);
      refetch();
    } else {
      showError(response.getError() as ErrorResponse);
    }
  };

  const handleCreateDistrict = () => {
    setCreateFormErrors({});
    setCreateData({ name: '', description: '' });
    setShowCreateModal(true);
  };

  const showValidation = useErrorModalStore((s) => s.showValidation);

  const handleCreateSubmit = async () => {
    const messages: string[] = [];
    const errors: CreateDistrictFormErrors = {};
    if (!nationId) messages.push('Nation context is required.');
    if (!createData.name?.trim()) {
      messages.push('District name is required.');
      errors.name = ['District name is required.'];
    }
    if (!createData.description?.trim()) {
      messages.push('Description is required.');
      errors.description = ['Description is required.'];
    }
    if (messages.length > 0) {
      setCreateFormErrors(errors);
      showValidation(messages);
      return;
    }

    setFormLoading(true);
    setFormError(null);
    setCreateFormErrors({});
    try {
      const response = await createDistrict(parseInt(nationId!, 10), {
        name: createData.name,
        description: createData.description,
      });
      if (response.isSuccess()) {
        setShowCreateModal(false);
        refetch();
      } else {
        const err = response.getError() as ErrorResponse | undefined;
        const fieldErrors = (err?.errors as CreateDistrictFormErrors) ?? {};
        setCreateFormErrors(fieldErrors);
        const list = fieldErrors && typeof fieldErrors === 'object'
          ? Object.values(fieldErrors).flat().filter(Boolean)
          : [err?.detail || 'Failed to create district'];
        showValidation(list.length ? list : [err?.detail || 'Failed to create district']);
      }
    } catch (error) {
      setFormError('An unexpected error occurred');
    } finally {
      setFormLoading(false);
    }
  };

  const handleViewDistrict = (district: District) => {
    setViewDistrict(district);
    setFormData({ district_name: district.district_name, description: district.description || '' });
    setEditFormErrors({});
    setFormError(null);
    setShowViewModal(true);
  };

  const handleViewChurches = (district: District) => {
    const query = new URLSearchParams({
      districtName: district.district_name,
    });

    navigate(`/nation/${nationId}/churches?${query.toString()}`, {
      state: { districtId: district.district_id, districtName: district.district_name },
    });
  };

  const handleUpdateDistrict = async () => {
    const messages: string[] = [];
    const errors: CreateDistrictFormErrors = {};
    if (!nationId) messages.push('Nation context is required.');
    if (!viewDistrict) messages.push('No district selected.');
    if (!formData.district_name?.trim()) {
      messages.push('District name is required.');
      errors.name = ['District name is required.'];
    }
    if (messages.length > 0) {
      setEditFormErrors(errors);
      showValidation(messages);
      return;
    }

    setFormLoading(true);
    setFormError(null);
    setEditFormErrors({});
    try {
      const nid = nationId!;
      const district = viewDistrict!;
      const response = await updateDistrict(parseInt(nid, 10), district.district_id, {
        name: formData.district_name!,
        description: formData.description || '',
      });
      if (response.isSuccess()) {
        setShowViewModal(false);
        refetch();
      } else {
        const err = response.getError() as ErrorResponse | undefined;
        const fieldErrors = (err?.errors as CreateDistrictFormErrors) ?? {};
        setEditFormErrors(fieldErrors);
        setFormError(err?.detail || 'Failed to update district');
      }
    } catch (error) {
      setFormError('An unexpected error occurred');
    } finally {
      setFormLoading(false);
    }
  };

  // Table columns configuration
  const columns: DataTableColumn<District>[] = [
    ...(canBulkAssign ? [{
      key: 'select',
      label: (
        <Form.Check
          type="checkbox"
          checked={paginatedDistricts.length > 0 && paginatedDistricts.every((d) => bulkSelection.isSelected(d.district_id))}
          onChange={() => bulkSelection.toggleAll(paginatedDistricts, (d) => d.district_id)}
          aria-label="Select all districts"
        />
      ) as any,
      width: '40px',
      render: (_value: any, row: District) => (
        <Form.Check
          type="checkbox"
          checked={bulkSelection.isSelected(row.district_id)}
          onChange={(e) => {
            e.stopPropagation();
            bulkSelection.toggleItem(row.district_id);
          }}
          onClick={(e) => e.stopPropagation()}
          aria-label={`Select ${row.district_name}`}
        />
      ),
    }] : []),
    {
      key: 'district_name',
      label: 'District Name',
      role: 'title',
      render: (value: string) => (
        <div className="d-flex align-items-center">
          <FiMapPin className="me-2 text-muted" />
          <span className="fw-medium">{value}</span>
        </div>
      ),
      cardRender: (value: string) => value,
    },
    {
      key: 'is_active',
      label: 'Status',
      role: 'status',
      render: (_value: any, row: District) => <StatusPill active={!!row.is_active} />
    },
    {
      key: 'actions',
      label: 'Actions',
      className: 'text-end',
      role: 'actions',
      render: (_value: any, row: District) => (
        <TableActions
          aria-label={`Actions for district ${row.district_name}`}
          maxInline={3}
        >
          <TableActionButton
            icon={canEdit ? <FiEdit2 /> : <FiEye />}
            label={canEdit ? 'Edit' : 'View'}
            primary
            variant="outline-primary"
            onClick={() => handleViewDistrict(row)}
          />
          <TableActionButton
            icon={<FiHome />}
            label="Churches"
            primary
            variant="outline-primary"
            onClick={() => handleViewChurches(row)}
          />
          <TableActionButton
            icon={canManageTemplates ? <FiFileText aria-hidden="true" /> : <FiEye aria-hidden="true" />}
            label={canManageTemplates ? 'Manage Templates' : 'View Templates'}
            primary
            variant="outline-secondary"
            onClick={() => handleOpenTemplates(row)}
          />
          {row.is_active && canDisable && (
            <TableActionButton
              icon={<FiSlash />}
              label="Disable"
              variant="outline-secondary"
              className="ntcog-table-action-btn--danger"
              onClick={() => setStatusConfirm({ district: row, kind: 'disable' })}
            />
          )}
          {!row.is_active && canEnable && (
            <TableActionButton
              icon={<FiCheckCircle />}
              label="Enable"
              variant="outline-secondary"
              className="ntcog-table-action-btn--success"
              onClick={() => setStatusConfirm({ district: row, kind: 'enable' })}
            />
          )}
        </TableActions>
      )
    }
  ];

  return (
    <Container fluid className="py-4">
      <div className="d-flex flex-column flex-md-row justify-content-md-between align-items-md-center gap-3 mb-4">
        <div className="ntcog-page-title-block mb-0">
          <h1 className="ntcog-page-heading mb-1">{nationName} - Districts</h1>
          <p className="text-muted mb-0">Manage districts in {nationName}</p>
        </div>
        {canCreate && (
          <Button variant="primary" onClick={handleCreateDistrict}>
            <FiPlus className="me-1" />
            Create District
          </Button>
        )}
      </div>

      <div className="ntcog-list-search-section">
        <SearchBar
          placeholder="Search districts…"
          filterFields={DISTRICT_FILTER_FIELDS}
          initialQuery={searchQuery}
          initialFilters={activeFilters}
          onQueryChange={setSearchQuery}
          onFiltersApply={(f) => {
            setActiveFilters({ ...f, sortDir: normalizeSortDir(f.sortDir) });
          }}
          onClear={() => {
            setSearchQuery('');
            setActiveFilters({ sortDir: SORT_DIR_ASC });
            updateParams(
              withCatalogSort(
                {
                  ...buildDefaultListParams({ freetext: undefined }, ['freetext', 'districtName', 'churchName']),
                  sortDir: SORT_DIR_ASC,
                },
                SORT_BY_DISTRICT_NAME
              )
            );
          }}
          loading={loading}
          activeFilterCount={countContentFilters(activeFilters)}
          testId="nation-districts-search"
        />
      </div>

      {/* Error Display */}
      {error && (
        <Alert variant="danger" className="mb-4">
          {error}
        </Alert>
      )}

      <Card className="shadow-sm ntcog-card ntcog-list-table-card">
        <Card.Body className="p-0">
          <DataTable
            data={paginatedDistricts}
            columns={columns}
            rowNumberOffset={(currentPage - 1) * itemsPerPage}
            loading={loading}
            error={error}
            emptyMessage="No districts found"
            cardAriaLabel={(row) => `District ${row.district_name}`}
            striped hover responsive
          />
        </Card.Body>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <TablePaginationFooter
          className="mt-3"
          testId="nation-districts-pagination"
          info={`Showing ${start} to ${end} of ${scopedDistricts.length} districts`}
        >
          <PaginationBar
            page={currentPage}
            totalPages={totalPages}
            onPage={setCurrentPage}
          />
        </TablePaginationFooter>
      )}
      {/* Create District Modal */}
      <Modal show={showCreateModal} onHide={() => { setShowCreateModal(false); setFormError(null); setCreateFormErrors({}); }} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Create New District</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {formError && (
            <Alert variant="danger" className="mb-3">{formError}</Alert>
          )}
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>District Name <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="text"
                maxLength={255}
                placeholder="Enter district name"
                value={createData.name}
                onChange={(e) => {
                  setCreateData({ ...createData, name: e.target.value });
                  if (createFormErrors.name) setCreateFormErrors((prev) => ({ ...prev, name: undefined }));
                }}
                required
                isInvalid={!!createFormErrors.name}
              />
              <Form.Control.Feedback type="invalid">{createFormErrors.name?.[0]}</Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Description <span className="text-danger">*</span></Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                maxLength={255}
                placeholder="Enter district description"
                value={createData.description}
                onChange={(e) => {
                  setCreateData({ ...createData, description: e.target.value });
                  if (createFormErrors.description) setCreateFormErrors((prev) => ({ ...prev, description: undefined }));
                }}
                required
                isInvalid={!!createFormErrors.description}
              />
              <Form.Control.Feedback type="invalid">{createFormErrors.description?.[0]}</Form.Control.Feedback>
            </Form.Group>
          </Form>
          <div className="d-flex justify-content-end mt-3">
            <Button
              variant="primary"
              onClick={handleCreateSubmit}
              disabled={formLoading}
              aria-busy={formLoading}
            >
              {formLoading ? (
                <>
                  <Spinner as="span" animation="border" size="sm" className="me-2" />
                  Creating...
                </>
              ) : (
                <>
                  <FiPlus className="me-2" />
                  Create District
                </>
              )}
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      {/* View/Edit District Modal */}
      <Modal show={showViewModal} onHide={() => { setShowViewModal(false); setFormError(null); setEditFormErrors({}); }} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>{canEdit ? 'Edit District' : 'District Details'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {formError && (
            <Alert variant="danger" className="mb-3">{formError}</Alert>
          )}
          <Form noValidate>
            <Form.Group className="mb-3">
              <Form.Label>District Name <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="text"
                maxLength={100}
                value={formData.district_name}
                onChange={(e) => {
                  setFormData({ ...formData, district_name: e.target.value });
                  if (editFormErrors.name) setEditFormErrors((prev) => ({ ...prev, name: undefined }));
                }}
                required
                disabled={!canEdit}
                isInvalid={!!editFormErrors.name}
              />
              <Form.Control.Feedback type="invalid">{editFormErrors.name?.[0]}</Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                maxLength={255}
                value={formData.description}
                onChange={(e) => {
                  setFormData({ ...formData, description: e.target.value });
                  if (editFormErrors.description) setEditFormErrors((prev) => ({ ...prev, description: undefined }));
                }}
                disabled={!canEdit}
                isInvalid={!!editFormErrors.description}
              />
              <Form.Control.Feedback type="invalid">{editFormErrors.description?.[0]}</Form.Control.Feedback>
            </Form.Group>
          </Form>
          {canEdit && (
            <div className="d-flex justify-content-end mt-3">
              <Button
                variant="primary"
                onClick={handleUpdateDistrict}
                disabled={formLoading}
                aria-busy={formLoading}
              >
                {formLoading ? (
                  <>
                    <Spinner as="span" animation="border" size="sm" className="me-2" />
                    Updating...
                  </>
                ) : (
                  'Update District'
                )}
              </Button>
            </div>
          )}
        </Modal.Body>
      </Modal>

      <StatusChangeConfirmModal
        show={!!statusConfirm}
        kind={statusConfirm?.kind ?? 'disable'}
        entityLabel="district"
        entityName={statusConfirm?.district.district_name ?? ''}
        busy={statusBusy}
        onConfirm={handleStatusConfirm}
        onCancel={() => setStatusConfirm(null)}
        bodyText={statusConfirm?.kind === 'disable' ? (
          <>
            Are you sure you want to disable <strong>{statusConfirm?.district.district_name}</strong>? Its churches drop out of new-work selection lists. Existing data is unaffected.
          </>
        ) : undefined}
      />

      <DistrictTemplatesModal
        show={showTemplateModal}
        onHide={() => setShowTemplateModal(false)}
        district={selectedDistrictForTemplates}
        canManage={canManageTemplates}
        nationId={numericNationId || undefined}
      />

      {canBulkAssign && (
        <BulkAssignTemplatesModal
          show={showBulkModal}
          onHide={() => setShowBulkModal(false)}
          entities={selectedDistrictsForBulk}
          entityType="district"
          nationId={numericNationId}
          onCompleted={() => {
            bulkSelection.deselectAll();
            refetch();
          }}
        />
      )}

      {canBulkAssign && bulkSelection.selectedCount > 0 && (
        <div
          className="position-fixed bottom-0 start-50 translate-middle-x mb-4 p-3 bg-white shadow-lg rounded d-flex align-items-center gap-3"
          style={{ zIndex: 1050 }}
        >
          <Badge bg="primary">{bulkSelection.selectedCount} selected</Badge>
          <Button
            variant="primary"
            size="sm"
            onClick={() => setShowBulkModal(true)}
          >
            <FiCheckSquare className="me-2" />
            Bulk Assign Templates
          </Button>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => bulkSelection.deselectAll()}
          >
            Clear Selection
          </Button>
        </div>
      )}
    </Container>
  );
};

export default NationDistrictsPage;